import { useEffect, useState } from 'react';

import { Button, InfoMessage, Input, SelectBox, Text } from 'components';
import PaymentMethodSelectBox from './PaymentMethodSelectBox';
import { SUBSCRIPTION } from './utils';
import axios from 'common/AxiosAuth';
import ChangePassword from './ChangePassword';
import { InformationCircleOutlineIcon } from 'assets/svgs';
import { Message } from 'types/message';
import { useTranslation } from 'react-i18next';
import { getDecodeCookie } from 'common/Cookie';

const AccountInfoSection = () => {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [purpose, setPurpose] = useState<string>('');
  const [password, setPassword] = useState<string>('*********');
  const [payment, setPayment] = useState<string>('');
  const [isSaveButtonShown, setIsSaveButtonShown] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const [isChangePasswordModalOpened, setIsChangePasswordModalOpened] = useState(false);
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const { t } = useTranslation();

  // 로그인 정보에서 login_method를 가져오는 함수
  const userInfo = getDecodeCookie('accessToken');
  const isGoogleLogin = userInfo?.login_method === 'GOOGLE';

  const fetchAccountData = async () => {
    try {
      const response = await axios.get('/api/member/myPage');
      const data = response.data;

      setName(data.name || '');
      setEmail(data.email || '');
      setCompany(data.company || '');
      setCountry(data.country || '');
      setPurpose(data.purpose || '');

    } catch (error) {
      console.error('Error fetching account data:', error);
    }
  };

  const saveAccountData = async () => {
    try {
      const requestData: any = {
        name,
        company,
        country,
        purpose
      };
      await axios.patch('/api/member/myPage', requestData);
      setMessageArray((prev) => [
        ...prev,
        {
          index: messageArrayCount,
          icon: <InformationCircleOutlineIcon/>,
          text: t('myPage.account.updated')
        }
      ]);
      setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
      setMessageArrayCount((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving account data:', error);
    }
  };

  useEffect(() => {
    fetchAccountData();
  }, []);

  useEffect(() => {
    if (!isMounted) {
      setIsMounted(true);
    } else if (!isSaveButtonShown) {
      setIsSaveButtonShown(true);
    }
  }, [name, email, password, country, purpose, payment]);

  const handleChangePassword = () => {
    setIsChangePasswordModalOpened(false);
    setMessageArray((prev) => [
      ...prev,
      {
        index: messageArrayCount,
        icon: <InformationCircleOutlineIcon aria-hidden="true"/>,
        text: `Your password has been changed.`
      }
    ]);
    setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
    setMessageArrayCount((prev) => prev + 1);
  };

  const onClickDeleteButton = (index: number) => {
    setMessageArray((prev) => prev.filter((message) => message.index !== index));
  };

  return (
    <section className="flex w-full flex-col gap-4 rounded-2xl border border-border-primary bg-bg-layer3 p-4">
      <Text size="body1" color="black" weight="medium">
        {t('myPage.account.title')} {/* Account Information */}
      </Text>
      <Input label={t('myPage.account.name')} value={name} setValue={setName} />
      <Input label={t('myPage.account.company')} type="text" value={company} setValue={setCompany} />
      <Input label={t('myPage.account.country')} type="text" value={country} setValue={setCountry} />
      <Input label={t('myPage.account.purpose')} type="text" value={purpose} setValue={setPurpose} />
      <div className="flex flex-row gap-2">
      {!isGoogleLogin && (
        <>
          <Input label={t('myPage.account.password')} type="password" value={password} setValue={setPassword}/>
            <div className='flex items-end'>
              <Button 
                type="secondary" 
                size="medium" 
                onClick={() => setIsChangePasswordModalOpened(true)} 
                aria-label="Open change password modal"
              >
                {t('myPage.account.changePassword')}
              </Button>
            </div>
        </>
        )}
      </div>
      {isSaveButtonShown && (
        <>
          <hr />
          <div className="flex flex-row gap-2">
            <Button 
              type="secondary" 
              text={t('myPage.account.save')} 
              onClick={saveAccountData} 
              disabled={isButtonDisabled} 
              aria-label="Save changes to account"  
            />
          </div>
        </>
      )}
      {isChangePasswordModalOpened && (
        <ChangePassword
          isOpen={isChangePasswordModalOpened}
          onConfirm={handleChangePassword}
          onCancel={() => setIsChangePasswordModalOpened(false)}
        />
      )}
      <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
    </section>
  );
};

export default AccountInfoSection;